import {
  POI_CHECK_REQUIRED,
  DECLINED,
  REFERRED,
  CANCELLED,
  SERVICE_UNAVAILABLE,
  APPROVED,
  CONDITIONALLY_APPROVED,
  QUOTE_INITIAL,
  QUOTE_DECLINE,
  IDV_CHECK_REQUIRED,
  CREATED,
  SAVED,
  APPLICATION_NOT_FOUND,
  PENDING,
  PROCESSING_ACCEPTANCE,
  IDV_CHECK_PENDING,
  POI_CHECK_PENDING,
  PENDING_CUSTOMER_ACCEPTANCE,
  BIOMETRICS_CHECK_REQUIRED,
  BIOMETRICS_STARTED,
  CCR_REQUIRED,
  CCR_PENDING,
  POI_BIO_CHECK_REQUIRED,
  OKTA_EMAIL_FACTOR,
  PROOF_OF_ADDRESS,
  PROOF_OF_ADDRESS_AND_PROOF_OF_INCOME,
  ANTI_MONEY_LAUNDERING,
  ANTI_MONEY_LAUNDERING_AND_PROOF_OF_INCOME,
  PROOF_OF_INCOME,
  BIOMETRICS_CONSENT_PENDING,
  BALANCE_TRANSFER,
} from 'store';
import {
  STEP_PROCESSING_BIOMETRICS,
  STEP_PROCESSING,
  STEP_PROCESSING_ACCEPTANCE,
  STEP_EIDV,
  STEP_VERIFY_INCOME,
  STEP_PROOF_OF_ADDRESS,
  STEP_CONFIRM_CREDIT_LIMIT,
  STEP_OUTCOME,
  STEP_PROCESSING_PENDING_POI,
  STEP_PROCESSING_IDV,
  STEP_DVS_CONSENT,
  STEP_RESUME_APPLICATION_EXPIRED,
  STEP_LOAN_INFORMATION,
  STEP_PROCESSING_LOAN_INFORMATION,
  STEP_AUS_POST,
  STEP_LATITUDE_ID_INTRODUCTION,
  STEP_VERIFY_INSTORE,
  STEP_VERIFY_APPLICATION_MOBILE,
  STEP_ID_DOC_UPLOAD,
  STEP_FACIAL_BIOMETRICS_CONSENT,
  STEP_CREDIT_LIMIT_ALLOCATION,
} from '_config';
import { byCountry, isAU, isCreditLine, isGemAU, isGO, isNZ } from 'utils';
import { isFeatureOn } from 'featureToggles';

function isPostSubmission(status) {
  return ![CREATED, SAVED, PENDING, QUOTE_INITIAL, QUOTE_DECLINE, DECLINED, CCR_REQUIRED].includes(status);
}

function isPostSaveApplication(status) {
  return ![CREATED, SAVED, PENDING].includes(status);
}

// EIDV methods available on /check-your-identity for NZ. Each corresponds to
// an `idvAttempts` flag on the application record. NZDriversLicence is handled
// via the DVS/upfront flow and is intentionally not part of the EIDV menu.
export const NZ_EIDV_METHODS = ['NZPassport', 'AUPassport', 'NZBirthCertificate', 'NZCitizenship'];

/**
 * @param {object} application
 * @param {object} storeState
 * @returns {string}
 */
export function calculateNextStep(application, storeState = {}) {
  const verifiedViaEmail = storeState.oktaFactor === OKTA_EMAIL_FACTOR;
  if (
    ((isFeatureOn('emailVerificationFallback') && !storeState.oktaProfileHealthy) ||
      (isFeatureOn('smsEmailVerificationOptions') && verifiedViaEmail)) &&
    !storeState.didApplicationMobileVerification &&
    ((isNZ() && isPostSaveApplication(application.status)) || (isAU() && isPostSubmission(application.status)))
  ) {
    // when user is without a verified mobile number on their Okta profile
    // verify mobile via SMS OTP at stages:
    //  - AU: post-submission after /confirm-credit-limit and /loan-information pages
    //  - NZ: post-save after /verify-income page
    return STEP_VERIFY_APPLICATION_MOBILE;
  }

  if (
    isPostSubmission(application.status) &&
    isFeatureOn('emailVerification') &&
    (!storeState.isLatitudeIdPasswordSet || !storeState.isEmailVerified)
  ) {
    // REDIRECT to Latitude ID steps if there are missing steps and the application is not in the specific status
    return STEP_LATITUDE_ID_INTRODUCTION;
  }

  switch (application.status) {
    case APPLICATION_NOT_FOUND: {
      return STEP_RESUME_APPLICATION_EXPIRED;
    }
    case BIOMETRICS_CHECK_REQUIRED:
    case BIOMETRICS_STARTED: {
      return STEP_PROCESSING_BIOMETRICS;
    }
    case SAVED:
    case PENDING: {
      return STEP_PROCESSING;
    }
    case PROCESSING_ACCEPTANCE: {
      return STEP_PROCESSING_ACCEPTANCE;
    }
    case IDV_CHECK_PENDING: {
      return STEP_PROCESSING_IDV;
    }
    case POI_CHECK_PENDING: {
      return STEP_PROCESSING_PENDING_POI;
    }
    case CCR_REQUIRED: {
      return STEP_LOAN_INFORMATION;
    }
    case CCR_PENDING: {
      return STEP_PROCESSING_LOAN_INFORMATION;
    }
    case QUOTE_INITIAL:
      if (application?.statusReason === BALANCE_TRANSFER) {
        return STEP_CREDIT_LIMIT_ALLOCATION;
      }
      return byCountry({
        AU: STEP_CONFIRM_CREDIT_LIMIT,
        NZ: STEP_CONFIRM_CREDIT_LIMIT,
      });
    case QUOTE_DECLINE: {
      return byCountry({
        AU: STEP_CONFIRM_CREDIT_LIMIT,
        NZ: STEP_CONFIRM_CREDIT_LIMIT,
      });
    }
    case IDV_CHECK_REQUIRED: {
      return isAU() ? calculateNextStepForAuIdv(application) : calculateNextStepForNzIdv(application);
    }
    case POI_BIO_CHECK_REQUIRED:
    case POI_CHECK_REQUIRED: {
      return STEP_VERIFY_INCOME;
    }
    case BIOMETRICS_CONSENT_PENDING: {
      return STEP_FACIAL_BIOMETRICS_CONSENT;
    }
    case PENDING_CUSTOMER_ACCEPTANCE: {
      throw new Error(
        `Unexpected application status: PENDING_CUSTOMER_ACCEPTANCE. Application ID: ${storeState.applicationId}.`,
      );
    }
    case APPROVED:
    case CONDITIONALLY_APPROVED: {
      if (
        isNZ() &&
        [PROOF_OF_INCOME, ANTI_MONEY_LAUNDERING_AND_PROOF_OF_INCOME, PROOF_OF_ADDRESS_AND_PROOF_OF_INCOME].includes(
          application?.statusReason,
        )
      ) {
        return calculateNextStepForNzIdv(application);
      }
      if (isNZ() && [ANTI_MONEY_LAUNDERING].includes(application?.statusReason)) {
        return calculateNextStepForNzIdv(application);
      }
      if (isNZ() && [PROOF_OF_ADDRESS].includes(application?.statusReason)) {
        return STEP_PROOF_OF_ADDRESS;
      }
      return STEP_OUTCOME;
    }
    case DECLINED:
    case REFERRED: {
      return STEP_OUTCOME;
    }
    case SERVICE_UNAVAILABLE:
    case CANCELLED: {
      // CANCELLED has no dedicated messaging yet, so it falls back to the generic
      // "something went wrong" outcome page, same as SERVICE_UNAVAILABLE (OBS-17249).
      return STEP_OUTCOME;
    }
    default:
      throw new Error(
        `Unexpected application status: ${application.status}. Application ID: ${storeState.applicationId}.`,
      );
  }
}

function calculateNextStepForNzIdv(application) {
  if (!application.finalDVSConsentAttempted && !application.dvsConsent) {
    return STEP_DVS_CONSENT;
  }

  const attempts = application.idvAttempts;
  const hasExhaustedEidvMethods = NZ_EIDV_METHODS.every(method => attempts?.[method] === true);
  const hasSelectedNone = attempts?.IdvNone === true;
  const noDvsConsent = application?.finalDVSConsentAttempted && !application?.dvsConsent;
  // facialBiometricsConsent should default to null. When the value is non-null, means user have seen the bio consent page and provided their decision.
  const noBioConsent = application?.facialBiometricsConsent === false;
  // fraudDrivenFacialBiometrics is default to null.
  // When fraud driven bio, Pavlova should set status to BIO_CHECK_REQUIRED. However, won't hurt to confirm notFraudBio just in case.
  const notFraudBio = application?.fraudDrivenFacialBiometrics === false;

  if (noDvsConsent || hasExhaustedEidvMethods || hasSelectedNone || noBioConsent || notFraudBio) {
    return STEP_ID_DOC_UPLOAD;
  }

  return STEP_EIDV;
}

function calculateNextStepForAuIdv(application) {
  if (
    // dvs consent not selected for the second time or idv attempts exhausted or idv none option triggered
    (!application.dvsConsent && application.finalDVSConsentAttempted) ||
    (application.medicareChecked && application.passportChecked) ||
    application.idvNoneChecked
  ) {
    if (isCreditLine() || isGO() || isGemAU()) {
      return STEP_VERIFY_INSTORE;
    }
    return STEP_AUS_POST;
  }
  if (application.dvsConsent) {
    return STEP_EIDV;
  }
  return STEP_DVS_CONSENT;
}
